import React from "react";
import styled from "styled-components";
import Head from "next/head";

import { Box, FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import PartnerForm from "@/components/Organisms/PartnerForm";
import YoutubePlayer from "@/components/Organisms/YoutubePlayer";
import IconText from "./IconText";
import DownloadPDFBanner from "../DownloadPDFBanner";

import Typography from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import Image from "next/image";

export const GetWhyShyft = (props) => {
  const { yogaDetailsData = [], hideOnMobile = false } = props || {};
  return (
    <WhyShyftWrapper hideOnMobile={hideOnMobile} flexDirection={"column"}>
      {yogaDetailsData?.brand_recognition?.why_shyft_text && (
        <WhyShyft flexDirection={"column"}>
          <Typography variant="title1" mb={"14px"} color={theme.colors.gray_90}>
            {yogaDetailsData?.brand_recognition?.why_shyft_heading}
          </Typography>
          <WhyShyftText
            variant="paragraph2"
            color={theme.colors.link2Color}
            fontFamily="var(--font-600)"
            fontSize={"16px"}
          >
            {yogaDetailsData?.brand_recognition?.why_shyft_text}
          </WhyShyftText>
        </WhyShyft>
      )}
    </WhyShyftWrapper>
  );
};

const HowItWorks = ({ yogaDetailsData, callBackRef, urlsArguments }) => {
  const { title, description, uploadDate, thumbnailUrl, contentUrl, duration } =
    yogaDetailsData?.og_meta || {};
  const videoStructuredSchemaData = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title,
    description,
    uploadDate,
    thumbnailUrl,
    contentUrl,
    duration,
  };

  const {
    meta,
    title: bannerTitle,
    cta_title: ctaTitle,
  } = yogaDetailsData?.floating_banner || {};

  const { icon, campaign_slug: campaignSlug } = meta || {};

  return (
    <>
      {yogaDetailsData?.brand_recognition?.video && (
        <Head>
          <script
            defer
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(videoStructuredSchemaData),
            }}
          />
        </Head>
      )}
      <Wrapper flexDirection={"column"}>
        <Group alignItems={"stretch"}>
          <LCol flexDirection={"column"}>
            {yogaDetailsData?.brand_recognition?.video ? (
              <VideoBox width={"100%"}>
                <YoutubePlayer
                  videoUrl={yogaDetailsData?.brand_recognition?.video}
                />
              </VideoBox>
            ) : (
              yogaDetailsData?.brand_recognition?.image_mob && (
                <BrandImageContainer>
                  <Image
                    src={yogaDetailsData?.brand_recognition?.image_mob}
                    alt="brand recognition"
                    fill={true}
                  />
                </BrandImageContainer>
              )
            )}
            <GetWhyShyft hideOnMobile yogaDetailsData={yogaDetailsData} />
          </LCol>
          <Col>
            <CallBackFormBox ref={callBackRef}>
              <PartnerForm
                isYogaPage
                formTitle={
                  yogaDetailsData?.hero?.hero_signup_title ||
                  "Sign up for a trial class"
                }
                formSubtitle={
                  yogaDetailsData?.hero?.hero_signup_subtitle ||
                  "Pay now to book your spot - we'll get in touch to set you up"
                }
                classInfo={
                  !yogaDetailsData?.hero?.request_callback &&
                  "Trial classes have limited slots - this small fee helps reserve yours!"
                }
                needMoreInfo={
                  !yogaDetailsData?.hero?.request_callback &&
                  "Need more information? Request a call back"
                }
                isRequestCallbackPage={yogaDetailsData?.hero?.request_callback}
                defaultSelectedGoal={
                  yogaDetailsData?.hero?.hero_signup_primary_pricing
                    ?.package_slug
                }
                priceConstant={yogaDetailsData?.hero?.hero_cta_title_constant}
                price={{
                  ...(yogaDetailsData?.hero?.hero_signup_primary_pricing || {}),
                }}
                urlsArguments={urlsArguments}
                isFreeTrialPage={
                  yogaDetailsData?.hero?.free_trial_request || false
                }
              />
            </CallBackFormBox>
          </Col>
        </Group>
        {bannerTitle && (
          <DownloadPDFBanner
            icon={icon}
            title={bannerTitle}
            ctaTitle={ctaTitle}
            campaignSlug={campaignSlug}
          />
        )}
        <Group alignItems={"flex-start"}>
          <GetWhyShyft hideOnMobile={false} yogaDetailsData={yogaDetailsData} />
          <IconText data={yogaDetailsData?.features} />
        </Group>
      </Wrapper>
    </>
  );
};

export default HowItWorks;

const Wrapper = styled(FlexDiv)`
  background-color: ${theme.colors.lightBlue};
  padding: 54px 0;
  @media only screen and (max-width: 1169px) {
    background-color: ${theme.colors.white};
    padding: 0;
  }
`;

const Col = styled(FlexDiv)`
  max-width: 50%;
  padding: 0 15px;
  width: 100%;
  justify-content: flex-end;
`;

const LCol = styled(FlexDiv)`
  max-width: 50%;
  padding: 0 15px;
  width: 100%;
  justify-content: flex-start;
`;

const CallBackFormBox = styled(FlexDiv)`
  background-color: ${theme.colors.white};
  padding: 35px;
  border: 1px solid ${theme.colors.border1};
  border-radius: 10px;
  max-width: 502px;
  width: 100%;
  height: fit-content;
`;

const Group = styled(FlexContainer)`
  @media only screen and (max-width: 1169px) {
    flex-direction: column;
    padding: 0;
    ${LCol} {
      padding: 15px 15px 0 15px;
    }
    ${Col} {
      ${CallBackFormBox} {
        border: 0;
        padding: 35px 20px;
      }
    }
    ${Col},${LCol} {
      max-width: 100%;
      justify-content: center;
    }
  }
`;

const VideoBox = styled(Box)``;

const WhyShyft = styled(FlexContainer)`
  text-align: left;
  align-items: left;
  background-color: ${theme.colors.white};
  padding: 22px 28px;
  border: 1px solid ${theme.colors.border1};
  border-radius: 10px;
  @media only screen and (max-width: 1169px) {
    background-color: transparent;
    border: 0;
    padding: 0;
  }
`;

const WhyShyftText = styled(Typography)`
  @media only screen and (max-width: 639px) {
    line-height: 24px;
  }
`;

const WhyShyftWrapper = styled(FlexDiv)`
  padding-top: 53px;
  ${({ hideOnMobile }) =>
    hideOnMobile &&
    `
      @media only screen and (max-width: 1169px) {
        display: none;
      }
  `};
  ${({ hideOnMobile }) =>
    !hideOnMobile &&
    `
    display: none;
    @media only screen and (max-width: 1169px) {
      display: flex;
      background-color: ${theme.colors.lightBlue};
      padding: 41px 15px 0;
      margin-bottom: 0;
    }
  `};
`;

const BrandImageContainer = styled(FlexDiv)`
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  position: relative;
  padding-top: 53.25%;
  width: 100%;
  img {
    object-fit: cover;
  }
`;
